import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SEOHead, { getBreadcrumbSchema } from '@/components/shared/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { newsArticles, newsArticlesList } from '@/data/newsData';
import { companyInfo } from '@/data/mock';
import { ArrowRight, Calendar, User, ChevronRight, Tag, ArrowLeft, Quote } from 'lucide-react';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  "headline": article.title,
  "description": article.seo.description,
  "author": {
    "@type": "Organization",
    "name": "Redspire Civil Engineering"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Redspire Civil Engineering",
    "logo": {
      "@type": "ImageObject",
      "url": "/logo.png"
    }
  },
  "datePublished": article.date,
  "image": article.heroImage,
  "mainEntityOfPage": `https://redspire.com/news/${article.slug}`
});

const NewsDetailPage = () => {
  const { articleId } = useParams();
  const article = newsArticles[articleId];

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  const breadcrumbItems = [
    { name: 'Home', url: 'https://redspire.com' },
    { name: 'News', url: 'https://redspire.com/news' },
    { name: article.title.substring(0, 40) + '...', url: `https://redspire.com/news/${article.slug}` }
  ];

  const combinedSchema = [
    getArticleSchema(article),
    getBreadcrumbSchema(breadcrumbItems)
  ];

  // Get related articles (same category, excluding current)
  const relatedArticles = newsArticlesList
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <Layout>
      <SEOHead
        title={article.seo.title}
        description={article.seo.description}
        keywords={article.seo.keywords}
        image={article.heroImage}
        url={`https://redspire.com/news/${article.slug}`}
        type="article"
        schema={combinedSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/60 to-gray-900/30" />
        </div>
        <div className="relative h-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/news" className="hover:text-white">News</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-red-400">Article</span>
            </nav>
            <Badge className="bg-red-600 hover:bg-red-700 mb-4">
              {article.category}
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-600" />
      </section>

      {/* Article Meta */}
      <section className="bg-gray-50 py-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-red-600" />
              <span>{formatDate(article.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-red-600" />
              <span>{article.author}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-red-600" />
              {article.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <p className="text-xl text-gray-700 leading-relaxed mb-8 font-medium">
            {article.content.intro}
          </p>

          {/* First image */}
          {article.content.images[0] && (
            <figure className="my-10">
              <img
                src={article.content.images[0].src}
                alt={article.content.images[0].alt}
                className="w-full rounded-xl shadow-lg"
              />
              <figcaption className="text-sm text-gray-500 mt-3 text-center">
                {article.content.images[0].alt}
              </figcaption>
            </figure>
          )}

          {/* First half of paragraphs */}
          {article.content.paragraphs.slice(0, 3).map((paragraph, index) => (
            <p key={index} className="text-gray-600 leading-relaxed mb-6">
              {paragraph}
            </p>
          ))}

          {/* Pull Quote */}
          {article.content.pullQuote && (
            <blockquote className="my-12 relative">
              <div className="bg-gray-50 rounded-2xl p-8 border-l-4 border-red-600">
                <Quote className="w-10 h-10 text-red-200 mb-4" />
                <p className="text-xl text-gray-800 font-medium leading-relaxed mb-4">
                  "{article.content.pullQuote.text}"
                </p>
                <cite className="text-red-600 font-medium not-italic">
                  — {article.content.pullQuote.attribution}
                </cite>
              </div>
            </blockquote>
          )}

          {/* Second image */}
          {article.content.images[1] && (
            <figure className="my-10">
              <img
                src={article.content.images[1].src}
                alt={article.content.images[1].alt}
                className="w-full rounded-xl shadow-lg"
              />
              <figcaption className="text-sm text-gray-500 mt-3 text-center">
                {article.content.images[1].alt}
              </figcaption>
            </figure>
          )}

          {/* Second half of paragraphs */}
          {article.content.paragraphs.slice(3, 6).map((paragraph, index) => (
            <p key={index} className="text-gray-600 leading-relaxed mb-6">
              {paragraph}
            </p>
          ))}

          {/* Third image */}
          {article.content.images[2] && (
            <figure className="my-10">
              <img
                src={article.content.images[2].src}
                alt={article.content.images[2].alt}
                className="w-full rounded-xl shadow-lg"
              />
              <figcaption className="text-sm text-gray-500 mt-3 text-center">
                {article.content.images[2].alt}
              </figcaption>
            </figure>
          )}

          {/* Remaining paragraphs */}
          {article.content.paragraphs.slice(6).map((paragraph, index) => (
            <p key={index} className="text-gray-600 leading-relaxed mb-6">
              {paragraph}
            </p>
          ))}

          {/* Related Services */}
          {article.relatedServices && article.relatedServices.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Services</h3>
              <div className="flex flex-wrap gap-3">
                {article.relatedServices.map((serviceId, index) => (
                  <Link key={index} to={`/services/${serviceId}`}>
                    <Badge variant="outline" className="hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer">
                      {serviceId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link key={relatedArticle.id} to={relatedArticle.href}>
                  <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 h-full">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={relatedArticle.image}
                        alt={relatedArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-red-600 hover:bg-red-700">
                        {relatedArticle.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-sm text-gray-500 mb-2">{formatDate(relatedArticle.date)}</p>
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2">
                        {relatedArticle.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to News / CTA */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <Link to="/news">
              <Button variant="outline" className="group">
                <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to News
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white group">
                Contact Redspire
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsDetailPage;
